import FileInfo from "files/types/interfaces/fileInfo";
import { apiClient, apiMutate } from "~/utils/apiClient";

export const FILE_BROWSER_API = import.meta.env.VITE_FILE_BROWSER_API;

export interface TokenPageMetadata {
  next_token: string | null;
  previous_token: string | null;
}

export interface DirectoryPage {
  items: FileInfo[];
  /** Only the first page's `current_file` is authoritative. */
  metadata: TokenPageMetadata & { current_file: FileInfo | null };
}

export interface SearchPage {
  items: FileInfo[];
  metadata: TokenPageMetadata & { truncated: boolean };
}

export interface PresignedUrlItem {
  key: string;
  url: string;
  expires_at: string;
}

/** Returns the URL of the first item. Throws if the response has none. */
export const firstPresignedUrl = (items: PresignedUrlItem[] | null | undefined): string => {
  const url = items?.[0]?.url;
  if (!url) {
    throw new Error("No presigned URL in response");
  }
  return url;
};

export interface MultipartPartUrl {
  part_num: number;
  url: string;
}

export interface MultipartUploadSession {
  upload_id: string;
  key: string;
  parts: MultipartPartUrl[];
}

export interface UploadPart {
  etag: string;
  part_num: number;
}

const escapeXml = (value: string) =>
  value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

const buildCompleteMultipartUploadXml = (parts: UploadPart[]) => {
  const partsXml = [...parts]
    .sort((a, b) => a.part_num - b.part_num)
    .map(
      ({ part_num, etag }) =>
        `<Part><PartNumber>${part_num}</PartNumber><ETag>${escapeXml(etag)}</ETag></Part>`
    )
    .join("");
  return `<CompleteMultipartUpload>${partsXml}</CompleteMultipartUpload>`;
};

/**
 * Completes a multipart upload: gets a presigned complete URL, then POSTs the
 * part list to S3 directly.
 */
export const completeMultipartUpload = async (
  key: string,
  uploadId: string,
  parts: UploadPart[],
  signal?: AbortSignal
): Promise<void> => {
  const onErrorMessage = "Unable to complete upload";
  const { url } = await apiMutate<PresignedUrlItem>(
    `${FILE_BROWSER_API}/multipart-uploads/${encodeURIComponent(uploadId)}/complete-url`,
    "POST",
    { key },
    { signal, onErrorMessage }
  );

  const s3Body = await apiClient<string>(url, {
    method: "POST",
    signal,
    body: buildCompleteMultipartUploadXml(parts),
    expectsText: true,
    onErrorMessage,
  });
  // S3 can return 200 for CompleteMultipartUpload and report the failure in the body.
  // Keys are XML-escaped in a success body, so a literal `<Error>` only appears in an error document.
  if (s3Body.includes("<Error>")) {
    throw new Error(onErrorMessage);
  }
};
